import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import DataUpload from './DataUpload.vue';
import DataTutorial from './DataTutorial.vue';
import IntegrationImport from './IntegrationImport.vue';
import ProgressIndicator from 'src/components/widgets/ProgressIndicator.vue';
import RemapColumnModal from 'src/components/widgets/RemapColumnModal/RemapColumnModal.vue';
import SchemaTable from './SchemaTable.vue';
import { FIELD_TYPES_INDEX } from "components/project/create/SchemaUtils";
import UniqueIdFieldsModal from 'components/widgets/UniqueIdFieldsModal/UniqueIdFieldsModal.vue';
import { makeExtraTypeOptions as makeExtraTypeOptionsForUniqueId } from 'components/widgets/UniqueIdFieldsModal/UniqueIdUtils';
import { makeExtraTypeOptions as makeExtraTypeOptionsForRemapColumns } from 'components/widgets/RemapColumnModal/RemapColumnUtils';
import Project from 'src/api/project';
import store from 'src/store';
import DataUtils from 'src/utils/data';
import ToggleSlider from 'src/components/widgets/ToggleSlider.vue';
import ObjectBrowser from 'src/components/project/create/ObjectBrowser.vue';
import FormatUtils from 'src/utils/formatters';
import { CLEAR_REQUEST_ERRORS, FETCH_INTEGRATIONS } from 'src/store/types';
import RadioButtons from "components/widgets/RadioButtons.vue";
import { BfButton, BfSpinner } from 'components/Butterfly';
export default Vue.extend({
    components: {
        RadioButtons, BfButton, BfSpinner,
        RemapColumnModal, DataUpload, DataTutorial, IntegrationImport, ProgressIndicator, SchemaTable, ToggleSlider,
        ObjectBrowser, UniqueIdFieldsModal,
    },
    beforeRouteEnter(to, from, next) {
        store.dispatch({ type: FETCH_INTEGRATIONS }).then(() => {
            next();
        }).catch(() => {
            next(false);
        });
    },
    data() {
        return {
            chrysalisRef: null,
            isLoading: false,
            isMounted: false,
            isFileUploading: false,
            name: '',
            nameError: false,
            nameExistsError: false,
            samples: null,
            frozenSamples: null,
            suggestedSchema: [],
            collapseIgnored: false,
            schema: [],
            uniqueFields: [],
            step: 1,
            useIntegration: false,
            showAddField: false,
            showUniqueIdFieldsModal: false,
            customColumns: [],
            integrationSettings: {},
            autoAddFilesEnabled: false,
            selectedS3Folder: '',
            selectedGCSFolder: '',
            showFileBrowser: false,
            sentimentEnabled: true,
            skipBadDates: false,
            piiClean: false,
            topLevelFolder: false,
            autoAddPartials: false,
            autoAddInterval: "every day",
            selectedWeekStart: "Sunday",
            weekStartDefault: "Sunday",
            weekStartOptions: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            exportStartTime: new Date(),
            autoAddIntervalOptions: {
                'every hour': {
                    'every': 1,
                    'period': 'hours',
                },
                'every day': {
                    'every': 1,
                    'period': 'days',
                },
                'every 7 days': {
                    'every': 7,
                    'period': 'days',
                },
            },
            selectedSentimentValue: process.env.APP_ENV === 'production' ? "plumeria_v3" : "textblob",
            sentimentOptions: [
                { value: "plumeria_v3", label: "Kapiche Plumeria V3 - NextGen 🌺" },
                { value: "plumeria_v2", label: "Kapiche Plumeria V2 🌺" },
                { value: "plumeria", label: "Kapiche Plumeria 🌺" },
                { value: "google", label: "Google Cloud Natural Language (may be slow on larger datasets)" },
                { value: "vader", label: "VADER (best for short-form social media)" },
                { value: "textblob", label: "TextBlob (fast, poor accuracy)" },
            ],
            schemaActionsLoading: {
                ignoreAll: false,
                reset: false,
                collapseIgnored: false,
            },
            ignoreAllConfirmation: false,
        };
    },
    computed: {
        ...mapGetters(['dataUnits', 'domain', 'plan', 'featureFlags', 'currentSite', 'subscription']),
        autoAddSettingLabel() {
            return `Automatically import new responses from ${this.integrationSettings.provider?.type.toUpperCase()}`;
        },
        canGoBack() {
            return this.step > 1 || this.useIntegration || this.isMounted && this.$refs.dataUpload && this.$refs.dataUpload.fileSelected;
        },
        canGoNext() {
            if (this.step === 1 && this.useIntegration && this.$refs.integrationImport && !this.$refs.integrationImport.importError) {
                // No next button when importing an integration, unless we are in the confirm step
                return this.$refs.integrationImport.step === 'confirm';
            }
            return this.step > 1 || this.isMounted && this.$refs.dataUpload && this.$refs.dataUpload.uploadFinished;
        },
        integrationNumResponses() {
            return this.$refs.integrationImport.numResponses;
        },
        numRecords() {
            if (this.useIntegration) {
                return this.integrationNumResponses;
            }
            else if (this.isMounted) {
                return this.$refs.dataUpload.numResponses;
            }
            return null;
        },
        showDataUnits() {
            return this.plan === 'data-unit-plan';
        },
        fileRowLimit() {
            return Math.min(500000, this.subscription.projectRowLimit ?? Infinity);
        },
        fileColumnLimit() {
            return Math.min(500, this.subscription.projectColumnLimit ?? Infinity);
        },
        countNonIgnoredColumns() {
            return this.schema.filter(col => col.type !== -1).length;
        },
        allowedToProceed() {
            const ignoredColumns = this.schema.filter(col => col.type === -1);
            const hasUniqueFieldsInIgnoredColumns = this.uniqueFields.some(uniqueField => {
                return ignoredColumns.some(ignoredCol => ignoredCol.name === uniqueField);
            });
            return (this.countNonIgnoredColumns <= this.fileColumnLimit &&
                !hasUniqueFieldsInIgnoredColumns);
        },
        remapSourceColumns() {
            return this.customColumns.map(t => t.reference_field);
        },
        extraTypeOptions() {
            const uniqueIdTypeOptions = makeExtraTypeOptionsForUniqueId(this.uniqueFields);
            const remapColumnTypeOptions = makeExtraTypeOptionsForRemapColumns(this.remapSourceColumns);
            return new Map([...uniqueIdTypeOptions].concat([...remapColumnTypeOptions]));
        },
        autoAddPartialsEnabled() {
            if (!this.autoAddFilesEnabled) {
                return {
                    enabled: false,
                    helpMsg: 'Automatic imports must be enabled to turn this feature on.',
                };
            }
            if (this.uniqueFields.length === 0) {
                return {
                    enabled: false,
                    helpMsg: 'This feature is only available for projects that are created with a unique row identifier. '
                        + 'This is to prevent importing the same survey response multiple times.'
                };
            }
            return { enabled: true, helpMsg: '' };
        },
        integrationSettingsProviderType() {
            return this.integrationSettings?.provider?.type;
        }
    },
    watch: {
        showAddField(v) {
            if (v) {
                this.$analytics.track.project.openCustomColumnsModal();
            }
        },
        integrationSettingsProviderType(newVal) {
            this.handleProviderTypeChange(newVal);
        }
    },
    mounted() {
        this.isMounted = true; // refs available
        // Select default sentiment engine from the site profile
        const sentimentOptions = this.sentimentOptions.map(({ value }) => value);
        const defaultSentiment = this.currentSite?.default_sentiment_engine;
        const pii_default = this.currentSite?.pii_clean;
        this.weekStartDefault = this.currentSite?.week_start ?? "Sunday";
        this.selectedWeekStart = this.weekStartDefault;
        if (pii_default != null) {
            this.piiClean = pii_default;
        }
        if (sentimentOptions.includes(defaultSentiment)) {
            this.selectedSentimentValue = defaultSentiment;
        }
    },
    methods: {
        number: FormatUtils.number,
        setFolder(path, provider) {
            if (provider === 's3') {
                this.selectedS3Folder = path;
            }
            else if (provider === 'gcs') {
                this.selectedGCSFolder = path;
            }
        },
        setIntegrationProvider(provider) {
            this.$set(this.integrationSettings, 'provider', provider);
        },
        handleProviderTypeChange(newVal) {
            if (newVal === 'qualtrics') {
                this.uniqueFields = ['Response ID'];
            }
        },
        setIntegrationInfo(info) {
            this.integrationSettings.info = info;
        },
        setUseIntegration() {
            this.useIntegration = true;
            this.autoAddFilesEnabled = true;
        },
        toggleAutoAdd(event) {
            this.autoAddFilesEnabled = event;
            if (event === false && this.integrationSettings?.provider?.type === 'survey_monkey') {
                this.autoAddPartials = false;
            }
        },
        async createProject() {
            this.isLoading = true;
            let schema = []; // Schema defines fields for a project
            let mapping = {}; // Mapping defines how data columns map to project fields
            let mappingColumns = [];
            let mappingValues = [];
            // Populate real schema and mappings from this component's editable schema
            let index = 0;
            for (let item of Object.values(this.schema)) {
                mappingValues.push(item.type);
                mappingColumns.push(item.name);
                if (item.type > -1) {
                    let newCol = {
                        index: index,
                        name: item.name,
                        type: item.type
                    };
                    if (item.num_type) {
                        newCol.num_type = item.num_type;
                    }
                    schema.push(newCol);
                    index += 1;
                }
            }
            // Assemble payload
            let payload = {
                name: this.name,
                description: this.description,
                schema: schema,
                unique_fields: this.uniqueFields,
                sentiment_enabled: this.sentimentEnabled,
                sentiment_classifier: this.selectedSentimentValue,
                chrysalis_ref: this.chrysalisRef,
                numRecords: this.numRecords,
                skip_bad_dates: this.skipBadDates,
                pii_clean: this.piiClean,
                week_start: this.selectedWeekStart,
            };
            if (this.$refs.schemaTable.selectedDateType !== null) {
                payload.day_first_dates = this.$refs.schemaTable.selectedDateType === 'dayFirst';
            }
            if (this.useIntegration) {
                payload = Object.assign({
                    integration: this.integrationSettings.provider.type,
                    integration_info: this.integrationSettings.info,
                    integration_status: 1
                }, payload);
            }
            // Create the actual project
            try {
                const project = await Project.createProject(payload);
                this.projectId = project.id;
                let eventData = {
                    sentiment: this.sentimentEnabled,
                    skipBadDates: this.skipBadDates,
                    startWeekDay: this.startWeekDay,
                    piiClean: this.piiClean,
                    projectId: this.projectId,
                    fileSize: null,
                    integrationProvider: null
                };
                if (!this.useIntegration) {
                    eventData.fileSize = this.$refs.dataUpload.fileSize;
                    eventData.numResponses = this.$refs.dataUpload.numResponses;
                }
                else {
                    eventData.integrationProvider = this.integrationSettings.provider.type;
                    eventData.fileSize = this.$refs.integrationImport.objectSize;
                    eventData.numResponses = this.integrationNumResponses;
                }
                this.$analytics.track.project.create(eventData);
                this.$analytics.track.project.addData(eventData.fileSize, eventData.numResponses, eventData.projectId);
                // Add custom columns
                if (this.customColumns.length > 0) {
                    await Project.createTransformations(project.id, this.customColumns.map(c => {
                        return {
                            reference_field: c.reference_field,
                            name: c.name,
                            schema_type: c.schema_type,
                            transformations: c.transformations
                        };
                    }));
                }
                if (this.autoAddFilesEnabled) {
                    const data = {
                        'enabled': true,
                        'run_immediately': false,
                        'selected_folder': '',
                        'add_partials': this.autoAddPartials,
                        'auto_add_interval': this.autoAddIntervalOptions[this.autoAddInterval],
                        'export_start_time': this.exportStartTime,
                    };
                    if (this.integrationSettings?.provider?.type === 's3') {
                        data.selected_folder = this.selectedS3Folder;
                        data.top_level_only = this.topLevelFolder;
                    }
                    else if (this.integrationSettings?.provider?.type === 'gcs') {
                        data.selected_folder = this.selectedGCSFolder;
                    }
                    await Project.updateAutoAddFilesSetting(project.id, data);
                }
                // Map file to schema
                for (let [i, item] of mappingColumns.entries()) {
                    if (mappingValues[i] > -1) { // Is this an ignored column?
                        mapping[item] = project.schema.find(col => col.name === item).index;
                    }
                    else {
                        mapping[item] = -1;
                    }
                }
                Project.indexDataFiles(this.projectId, [this.chrysalisRef], mapping);
                await this.$router.push({ name: 'project-details', params: { projectId: project.id } });
            }
            finally {
                this.isLoading = false;
            }
        },
        // Go back to home route
        goHome() {
            this.$router.push({ name: 'home' });
        },
        // Go to previous wizard step
        goBack() {
            if (this.step === 1) {
                // Going back in step 1 means clearing state
                if (this.useIntegration) {
                    if (!this.$refs.integrationImport.goBack()) {
                        // IntegrationUpload has empty state, so switch back to upload type selection
                        this.useIntegration = false;
                        this.autoAddFilesEnabled = false;
                    }
                }
                else {
                    // Clear data file
                    this.$refs.dataUpload.clearUpload();
                }
            }
            else {
                this.step -= 1;
            }
        },
        // Go to next wizard step
        async goNext() {
            if (this.step === 1) {
                if (this.useIntegration) {
                    this.isLoading = true;
                    // Initiate async import of survey responses
                    this.$refs.integrationImport.runImport((dataFile, metadata) => {
                        // Load metadata and move to schema mappinges
                        this.onFileReady(dataFile, metadata.headers, metadata.samples);
                        this.isLoading = false;
                        this.step = 2;
                    }, () => {
                        // Error handler
                        this.isLoading = false;
                    });
                }
                else {
                    this.step = 2;
                }
            }
            else if (this.step === 2 && this.$refs.schemaTable.isValid()) {
                // Schema selection validation
                this.$refs.schemaTable.checkForAmbiguousDates(() => {
                    this.step += 1;
                });
            }
            else if (this.step === 3) {
                this.nameError = false;
                this.nameExistsError = false;
                if (this.name.length === 0) {
                    this.nameError = true;
                }
                else {
                    this.isLoading = true;
                    try {
                        await Project.checkProjectName(this.name);
                        await this.createProject();
                    }
                    catch (response) {
                        // Check for duplicate name error
                        let error = response.body['non_field_errors'] || [''];
                        if (error[0].toLowerCase().indexOf('already exists') >= 0) {
                            this.nameExistsError = true;
                            this.$store.dispatch(CLEAR_REQUEST_ERRORS); // custom error handling
                        }
                    }
                    finally {
                        this.isLoading = false;
                    }
                }
            }
        },
        onUploadStarted() {
            this.isFileUploading = true;
        },
        onUploadFailed() {
            this.isFileUploading = false;
        },
        // When DataUpload or IntegrationImport component is ready we have access to file metadata
        onFileReady(dataFile, headers, samples) {
            this.isFileUploading = false;
            this.chrysalisRef = dataFile.id;
            this.schema = Object.assign([], dataFile.schema);
            this.suggestedSchema = Object.assign([], _.cloneDeep(dataFile.schema));
            this.uniqueFields = this.uniqueFields || [];
            this.samples = samples;
            this.frozenSamples = DataUtils.deepFreeze(samples, true);
        },
        // Manually show the data tutorial
        showDataTutorial() {
            this.$refs.dataTutorial.show('organise');
        },
        addOrUpdateCustomColumn({ id, transformations, referenceField, newName, type }) {
            const transformationMap = transformations.reduce((map, cur) => {
                map[cur.from.toLowerCase()] = cur.to;
                return map;
            }, {});
            const col = this.customColumns.find(t => t.id === id);
            if (col) {
                // We are updating a custom column
                // The only thing we can change are the transformations
                col.transformations = transformations;
                // This will require us to update the schema table & preview samples
                const targetIndex = this.schema.findIndex(f => f.name === col.name);
                const sourceIndex = referenceField.index;
                for (const sample of this.samples) {
                    sample[targetIndex] = transformationMap[sample[sourceIndex].toLowerCase()] || sample[sourceIndex];
                }
                this.frozenSamples = DataUtils.deepFreeze(this.samples, true);
            }
            else {
                // We are adding a new column
                this.customColumns.push({
                    id: id,
                    reference_field: referenceField.name,
                    name: newName,
                    schema_type: type,
                    transformations: transformations,
                });
                // The target field for the custom column needs to be added to the project schema definition
                this.schema.push({
                    index: this.schema.length,
                    name: newName,
                    type: type,
                });
                // We now need to convert the samples we have so that the user can
                // preview what the results of their transformation is. This is
                // duplicated in the backend, but since this is just the preview,
                // we are going to just transform them here for speed and simplicity.
                const idx = referenceField.index;
                for (const sample of this.samples) {
                    const old_sample = sample[idx];
                    const new_sample = transformationMap[old_sample.toLowerCase()] || old_sample;
                    sample.push(new_sample);
                }
                this.frozenSamples = DataUtils.deepFreeze(this.samples, true);
            }
            this.$analytics.track.project.addOrUpdateCustomColumn(col === undefined, col ? col.reference_field : referenceField.name, col ? col.name : newName, FIELD_TYPES_INDEX[col ? col.schema_type : type].label, transformations.length);
        },
        removeCustomColumn(id) {
            const col = this.customColumns.find(t => t.id === id);
            if (!col) {
                return;
            }
            this.customColumns = this.customColumns.filter(t => t.id != id);
            // Update schema table and samples
            const fieldIndex = this.schema.findIndex(f => f.name === col.name);
            this.schema.splice(fieldIndex, 1);
            for (const sample of this.samples) {
                sample.splice(fieldIndex, 1);
            }
            this.frozenSamples = DataUtils.deepFreeze(this.samples, true);
            this.$analytics.track.project.removeCustomColumn(col.name, FIELD_TYPES_INDEX[col.schema_type].label);
        },
        changeSentimentOption(value) {
            this.selectedSentimentValue = value;
        },
        updateSchemaType(index, newType) {
            this.schema[index].type = newType;
            this.schema[index].typename = Project.COLUMN_INDEXED_TYPES.get(newType);
        },
        setAllFieldsToIgnore() {
            this.schemaActionsLoading.ignoreAll = true;
            setTimeout(() => {
                try {
                    this.schema.map(col => {
                        if (!this.uniqueFields.includes(col.name)) {
                            col.type = -1;
                            col.typename = 'IGNORE';
                        }
                    });
                }
                finally {
                    this.schemaActionsLoading.ignoreAll = false;
                }
            });
        },
        setAllFieldsToInferredTypes() {
            this.schemaActionsLoading.reset = true;
            setTimeout(() => {
                try {
                    const clonedSuggested = _.cloneDeep(this.suggestedSchema);
                    this.schema = clonedSuggested;
                }
                finally {
                    this.schemaActionsLoading.reset = false;
                }
            });
        },
        handleExpandClick() {
            this.schemaActionsLoading.collapseIgnored = true;
            setTimeout(() => {
                try {
                    this.collapseIgnored = !this.collapseIgnored;
                }
                finally {
                    this.schemaActionsLoading.collapseIgnored = false;
                }
            });
        }
    }
});
